import { useEffect, useState } from 'react';
import DropdownFilter, { type OptionType } from '../../../../../components/dropdowns/DropdownFilter';
import { useData } from '../../../../../hooks/useData';
import StandardButton from '../../../../../components/buttons/StandardButton';
import { type Location } from '@/types/Location';
import { NotificationDisplayMap, NotificationType } from '@/constants';
import { type UserPreferenceRequest, UserPreference } from '@/types/UserPreference';
import { postUserPreference, deleteUserPreference, getUserPreference, getUserDevices } from '@/utils/api';
import { toast } from 'sonner';
import SectionTitle from '@/components/settings/admin/SectionTitle';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

export interface BatchRequests {
    sub: UserPreferenceRequest;
    unsub: UserPreferenceRequest;
}

const NotificationConfiguration: React.FC = () => {
    const [selectedLocations, setSelectedLocations] = useState<Location[]>([]);
    const [notificationType, setNotificationType] = useState<OptionType>();
    const [currUserPrefs, setCurrUserPrefs] = useState<UserPreference[]>([]);
    const [userDeviceTokens, setUserDeviceTokens] = useState<string[] | null>(null);
    const [newRequests, setNewRequests] = useState<BatchRequests>();
    const [notificationOptions, setNotificationOptions] = useState<OptionType[]>([]);
    const [saving, setSaving] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    const {
        selectedFranchise,
        displayedLocations,
        franchiseLocations,
        userLocations,
        handleFilter,
    } = useData();

    useEffect(() => {
        setNotificationOptions(getNotificationOptions());
    }, []);

    useEffect(() => {
        fetchUserPreferences();
        fetchUserDevices();
    }, [notificationOptions]);

    useEffect(() => {
        const reset = () => {
            setSaving(false);
            fetchUserPreferences();
        };
        if (!newRequests) { return; }
        if (newRequests.sub.locationIds.length > 0) {
            setSaving(true);
            postUserPreference(newRequests.sub).finally(() => {
                reset();
            });
        }
        if (newRequests.unsub.locationIds.length > 0) {
            setSaving(true);
            deleteUserPreference(newRequests.unsub).finally(() => {
                reset();
            });
        }
    }, [newRequests]);

    // Get existing user preferences
    const fetchUserPreferences = () => {
        setLoading(true)
        getUserPreference()
            .then((preferences) => {
                setCurrUserPrefs(preferences);
                const preSelectedType = notificationOptions[0]; // default notification type to first option
                const preSelectedLocations = userLocations
                    .filter(l => preferences
                        .filter(p => p.notificationType == preSelectedType.id) // only of selected notification type
                        .map(p => p.locationId).includes(l.id)); // get only subscribed locations from franchise
                setSelectedLocations(preSelectedLocations);
                setNotificationType(preSelectedType);
            })
            .catch(() => {
                toast.error('Failed to fetch user preferences');
            })
            .finally(() => setLoading(false));
    };

    // Get existing user devices
    const fetchUserDevices = () => {
        getUserDevices().then((deviceTokens) => {
            setUserDeviceTokens(deviceTokens);
        })
            .catch(() => {
                toast.error('Failed to fetch user device tokens');
            })
    }

    const getNotificationOptions = (): OptionType[] => {
        const notificationTypeOptions = Object.values(NotificationType).filter(v => typeof v === 'string');
        let filteredOptions;
        if (import.meta.env.VITE_AWS_REGION.includes('us-')) {
            const euSpecificOptions: Set<NotificationType> = new Set([NotificationType.LOBBY_CLOSE, NotificationType.LOBBY_OPEN, NotificationType.LOBBY_LATE]);
            filteredOptions = notificationTypeOptions.filter((opt) => !euSpecificOptions.has(opt)); // Remove eu specific options
        } else {
            const usSpecificOptions: Set<NotificationType> = new Set([NotificationType.DT_SOS, NotificationType.DT_AVG_SOS, NotificationType.QUEUE_VOL]);
            filteredOptions = notificationTypeOptions.filter((opt) => !usSpecificOptions.has(opt)); // Remove us specific options
        }

        const options: OptionType[] = [];
        filteredOptions.forEach(n => options.push(
            { id: n.toString(), value: NotificationDisplayMap[n.toString() as keyof typeof NotificationDisplayMap] }
        ));
        return options;
    };

    const handleLocationChange = (id: string) => {
        const foundLocation = displayedLocations.find(l => l.id === id);
        if (!foundLocation) return;

        let newLocationList;
        if (selectedLocations.includes(foundLocation)) {
            // Remove the location if it already exists
            newLocationList = selectedLocations.filter(selectedL => selectedL.id !== foundLocation.id);
        } else {
            // Add the location if it does not exist
            newLocationList = [...selectedLocations, foundLocation];
        }

        // Update the state with the new locations
        setSelectedLocations(newLocationList);
    };

    const locationsToOptions = (locations: Location[]): OptionType[] => {
        const naturalSortOptions = (options: Array<{ id: string; value: string }>) => {
            const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
            return [...options].sort((a, b) => collator.compare(a.value, b.value));
        };
        const userFranchiseLocations = locations.filter(l => l.franchiseId === selectedFranchise?.id);
        const locationOptions = userFranchiseLocations.map(l => ({
            id: l.id,
            value: l.displayName,
        }));
        return naturalSortOptions(locationOptions);
    }

    const handleNotificationTypeChange = (notificationOption: OptionType) => {
        setNotificationType(notificationOption);
        const preSelectedLocations = userLocations
            .filter(l => l.franchiseId === selectedFranchise?.id) // only get franchise locations
            .filter(l => currUserPrefs
                .filter(p => p.notificationType == notificationOption.id)
                .map(p => p.locationId).includes(l.id)); // get only subscribed locations from franchise
        setSelectedLocations(preSelectedLocations);
    }

    const handleSave = () => {
        if (!notificationType) { return; }

        const idsOfCurrPrefs = currUserPrefs
            .filter(p => p.notificationType === notificationType.id)
            .map(p => p.locationId);

        // new subscriptions: in selected locations but not in current preferences
        const newSubLocationIds = selectedLocations.filter(loc => !idsOfCurrPrefs.includes(loc.id)).map(nsl => nsl.id);
        // new unsubscriptions: not in selected locations but in current preferences
        const newUnsubLocationIds = idsOfCurrPrefs
            .filter(id => !selectedLocations.map(l => l.id).includes(id)) // not currently selected
            .filter(id => franchiseLocations.map(l => l.locationId).includes(id)); // within the same franchise

        setNewRequests({
            sub: {
                notificationType: notificationType.id,
                locationIds: newSubLocationIds,
            },
            unsub: {
                notificationType: notificationType.id,
                locationIds: newUnsubLocationIds,
            }
        });
    };

    if (loading) {
        return (
            <section>
                <SectionTitle title="Push Notifications" />
                <LoadingSpinner />
            </section>);
    }

    return (
        <section>
            <SectionTitle title="Push Notifications" />
            <p className="text-slate-600 dark:text-slate-400 mb-2">
                Set real-time push notification settings here.
            </p>
            {(userDeviceTokens && userDeviceTokens.length === 0) ? (
                <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
                    ❌ No device tokens were detected. Please sign into the mobile app to register your device token.
                </p>
            ) : (
                (userDeviceTokens && (
                    <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
                        ✅ {userDeviceTokens?.length} device token{userDeviceTokens && userDeviceTokens.length > 1 ? "s" : ""} found.
                    </p>)
                )
            )}
            <div className="flex flex-wrap w-full gap-12">
                {/* Change Notification Type */}
                <div className="flex flex-col gap-4">
                    <label className="font-bold">Notification Type</label>
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-4">
                            {notificationOptions.map(option => (
                                <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="notificationType"
                                        value={option.id}
                                        checked={notificationType?.id === option.id}
                                        onChange={() => handleNotificationTypeChange(option)}
                                        className="form-radio text-blue-600"
                                        data-cy={`notification-type-radio-${option.id}`}
                                    // disabled={saving}
                                    />
                                    <span className="text-sm">{option.value}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                {/* Change Notification Preference */}
                <div className="flex flex-col gap-4">
                    <label className="font-bold">Notification Preference Updates</label>
                    <DropdownFilter
                        title={selectedLocations.length + ' locations'}
                        options={locationsToOptions(displayedLocations)}
                        selected={locationsToOptions(selectedLocations)}
                        handleChange={handleLocationChange}
                        selectAllFunction={() => setSelectedLocations(userLocations)}
                        deselectAllFunction={() => setSelectedLocations([])}
                        filterFunction={handleFilter}
                        dataCy={'dropdown-location-notification-multiple'}
                    />
                    <div className="flex flex-wrap gap-8 w-full">
                        <StandardButton onClick={handleSave} dataCy="update-notification-preference" disabled={saving || (notificationType == null)}>
                            Save Updates
                        </StandardButton>
                    </div>
                </div>
                {/* Notification Descriptions */}
                <div className="flex flex-col gap-4 max-w-96">
                    {notificationType?.id == 'DT_SOS' && (
                        <div>
                            <p className="font-semibold">Latest Drive Through Speed Notifications</p>
                            <p className="text-sm my-1 text-balance">Receive a notification every time a vehicle takes longer than the threshold to compelete an entire customer journey.</p>
                        </div>
                    )}
                    {notificationType?.id == 'DT_AVG_SOS' && (
                        <div>
                            <p className="font-semibold">Average Drive Through Speed Notifications</p>
                            <p className="text-sm my-1 text-balance">Receive a notification every time the 45 min average vehicle takes longer than the threshold to compelete an entire customer journey.</p>
                        </div>
                    )}
                    {notificationType?.id == 'QUEUE_VOL' && (
                        <div>
                            <p className="font-semibold">Current Drive Through Queue Length</p>
                            <p className="text-sm my-1 text-balance">Receive a notification every the pre-order queue gets longer than the threshold.</p>
                        </div>
                    )}
                    {notificationType?.id == 'LOBBY_VOL' && (
                        <div>
                            <p className="font-semibold">Lobby Volume Notifications</p>
                            <p className="text-sm my-1 text-balance">Receive a notification with there is more than the threshold amount of customers in the lobby.</p>
                        </div>
                    )}
                    {notificationType?.id == 'LOBBY_OPEN' && (
                        <div>
                            <p className="font-semibold">Lobby Open Time Notifications</p>
                            <p className="text-sm my-1 text-balance">Receive a notification with the timestamp when a restaurant opens after its specified opening time.</p>
                        </div>
                    )}
                    {notificationType?.id == 'LOBBY_LATE' && (
                        <div>
                            <p className="font-semibold">Lobby Late Open Notifications</p>
                            <p className="text-sm my-1 text-balance">Receive a notification when a restaurant is not open at its specified opening time.</p>
                        </div>
                    )}
                    {notificationType?.id == 'LOBBY_CLOSE' && (
                        <div>
                            <p className="font-semibold">Lobby Early Close Notifications</p>
                            <p className="text-sm my-1 text-balance">Receive a notification when a restaurant is closed before its specified closing time.</p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default NotificationConfiguration;
